import { UserAddOutlined } from '@ant-design/icons'
import { Button, Form } from 'antd'
import { t } from 'i18next'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { ModalBase, SelectParticipant } from '../../../../../components'
import { privacyTypes } from '../../../../../constants/constants'
import useWorkspaceDetails from '../../../../../hooks/useWorkspaceDetails'
import { notificationService, workspaceService } from '../../../../../services'
import { User } from '../../../../../store/reducers/userReducer'
import WorkspaceSettingsFooter from '../footer/WorkspaceSettingsFooter'

interface WorkspaceDetailsAddParticipantModalProps {
  disabled?: boolean
}

const WorkspaceDetailsAddParticipantModal = ({
  disabled,
}: WorkspaceDetailsAddParticipantModalProps) => {
  const [form] = Form.useForm()
  const [open, setOpen] = useState(false)
  const [selectLoading, setSelectLoading] = useState(false)
  const [confirmLoading, setConfirmLoading] = useState(false)
  const [permittedUsers, setPermittedUsers] = useState<User[]>([])

  const { workspaceId } = useParams()

  const {
    selectedWorkspace,
    refreshWorkspaceDetails,
    getPermittedUsersOfWorkspace,
  } = useWorkspaceDetails({
    workspaceId,
    preventFetch: true,
  })

  const fetchPermittedUsers = useCallback(async () => {
    setSelectLoading(true)
    try {
      const users = await getPermittedUsersOfWorkspace()
      setPermittedUsers(users)
    } catch (error) {
      console.error('Error fetching permitted users:', error)
    } finally {
      setSelectLoading(false)
    }
  }, [getPermittedUsersOfWorkspace])

  useEffect(() => {
    fetchPermittedUsers()
  }, [fetchPermittedUsers])

  const participantsArray = useMemo(
    () =>
      selectedWorkspace?.participants
        ? selectedWorkspace.participants.map((participant) => participant.id)
        : [],
    [selectedWorkspace]
  )

  const onClose = useCallback(() => {
    setOpen(false)
    setPermittedUsers([])
  }, [])

  const handleOpen = useCallback(() => {
    setOpen(true)
    fetchPermittedUsers()
  }, [fetchPermittedUsers])

  const handleOk = useCallback(() => {
    setConfirmLoading(true)
  }, [setConfirmLoading])

  const handleCancel = useCallback(() => {
    onClose()
  }, [onClose])

  const handleConfirm = useCallback(() => {
    setOpen(false)
    if (selectedWorkspace) {
      workspaceService
        .updateWorkspaceDetails({
          workspaceId: selectedWorkspace.id,
          participants: form.getFieldValue('participants'),
        })
        .then(() => {
          refreshWorkspaceDetails()
          setConfirmLoading(false)
          notificationService.notificationSuccess({
            messageToDisplay: t(
              'workspaceDetailsPage.workspaceSettingsPeoplePanel.addParticipantModal.addParticipantSuccess'
            ),
          })
        })
        .catch(() => {
          setConfirmLoading(false)
        })
    }
    return
  }, [
    selectedWorkspace,
    setConfirmLoading,
    setOpen,
    form,
    refreshWorkspaceDetails,
  ])

  return (
    <>
      <Button
        icon={<UserAddOutlined />}
        onClick={handleOpen}
        type="primary"
        disabled={
          disabled ||
          selectedWorkspace?.visibility === privacyTypes.ORGANIZATION
        }
      >
        {t(
          'workspaceDetailsPage.workspaceSettingsPeoplePanel.addParticipantModal.addPersonButton'
        )}
      </Button>
      <ModalBase
        key="main"
        title={t(
          'workspaceDetailsPage.workspaceSettingsPeoplePanel.addParticipantModal.title'
        )}
        open={open}
        onOk={handleOk}
        confirmLoading={confirmLoading}
        onCancel={handleCancel}
        forceRender
        footer={
          <WorkspaceSettingsFooter
            handleCancel={handleCancel}
            handleSubmit={handleConfirm}
            icon={<UserAddOutlined />}
            confirmButtonText={t('Confirm')}
          />
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label={t(
              'createWorkspaceModal.privacyStep.participantSelectHeader'
            )}
            name="participants"
            extra={
              t('createWorkspaceModal.privacyStep.extraInputPlaceholder') || ''
            }
          >
            <SelectParticipant
              disabled={
                selectedWorkspace?.visibility !== privacyTypes.PRIVATE ||
                selectLoading
              }
              initialParticipants={participantsArray}
              allParticipants={permittedUsers}
              disableRemove
              loading={selectLoading}
            />
          </Form.Item>
        </Form>
      </ModalBase>
    </>
  )
}

export default WorkspaceDetailsAddParticipantModal
